import type { AnnotationRecordResponse } from "@/hooks/useLabelingQueries";
import type { StudyMetadataResponse } from "@/utils/dicomMetadataLoader";
import { pixelIndicesToRuns } from "@/predictions/savedCardGeometry";
import {
  buildSegmentationPreviews,
  type SegmentationPreview,
} from "@/predictions/segmentationPreviews";

const dot = (a: readonly number[], b: readonly number[]) =>
  a.reduce((sum, value, i) => sum + value * b[i], 0);

/** Resolve stored mask coordinates to native DICOM, independently of renderer segment IDs. */
export function buildSavedSegmentationPreview(
  annotation: AnnotationRecordResponse,
  metadata: StudyMetadataResponse | null | undefined,
  condition: string,
  sequence: number,
  resolvedSliceIndex?: number,
): SegmentationPreview | null {
  const geometry = annotation.card_geometry;
  const plane = geometry?.representative;
  const affine = plane?.affine;
  const seriesId = annotation.series_instance_uid;
  const series = metadata?.series_metadata[seriesId];
  const instances = metadata?.series[seriesId];

  if (
    !geometry ||
    !plane ||
    !affine ||
    affine.length !== 12 ||
    !affine.every(Number.isFinite) ||
    !series ||
    !instances
  ) {
    return null;
  }
  const worldAt = (column: number, row: number) =>
    [0, 4, 8].map(
      (offset) =>
        affine[offset] * column +
        affine[offset + 1] * row +
        affine[offset + 2] * plane.sliceIndex +
        affine[offset + 3],
    );
  const anchor = worldAt(0, 0);
  // Match in world space, allowing sub-slice reconstruction/rounding offsets.
  // Pick the nearest plane, never the first plane inside a tolerance band.
  const candidates = instances
    .map((instance, sliceIndex) => {
      const orientation = instance.image_orientation_patient ?? series.image_orientation_patient;
      const origin = instance.image_position_patient;
      if (!orientation || !origin) return { sliceIndex, distance: Infinity };
      const [a, b, c, d, e, f] = orientation;
      return {
        sliceIndex,
        distance: Math.abs(
          dot(
            anchor.map((value, axis) => value - origin[axis]),
            [b * f - c * e, c * d - a * f, a * e - b * d],
          ),
        ),
      };
    })
    .sort((a, b) => a.distance - b.distance);
  for (const { sliceIndex } of resolvedSliceIndex === undefined
    ? candidates.slice(0, 1)
    : [{ sliceIndex: resolvedSliceIndex }]) {
    const instance = instances[sliceIndex];
    const origin = instance.image_position_patient;
    const orientation = instance.image_orientation_patient ?? series.image_orientation_patient;
    const spacing = (instance.pixel_spacing ?? series.pixel_spacing)?.split("\\").map(Number);
    const rows = instance.rows ?? plane.rows;
    const columns = instance.columns ?? plane.columns;
    if (
      !origin ||
      origin.length !== 3 ||
      !orientation ||
      orientation.length !== 6 ||
      !spacing ||
      spacing.length !== 2 ||
      !spacing.every((value) => Number.isFinite(value) && value > 0) ||
      !rows ||
      !columns
    ) {
      continue;
    }
    const x = orientation.slice(0, 3),
      y = orientation.slice(3, 6);
    const normal = [
      x[1] * y[2] - x[2] * y[1],
      x[2] * y[0] - x[0] * y[2],
      x[0] * y[1] - x[1] * y[0],
    ];
    if (Math.abs(Math.hypot(...normal) - 1) > 0.01) {
      continue;
    }
    const delta = (world: number[]) => world.map((value, axis) => value - origin[axis]);
    const neighborDistances = instances
      .filter((_, index) => index !== sliceIndex)
      .flatMap((other) =>
        other.image_position_patient
          ? [Math.abs(dot(delta(other.image_position_patient), normal))]
          : [],
      )
      .filter((distance) => distance > 0.001);
    const spacingBetweenPlanes = neighborDistances.length
      ? Math.min(...neighborDistances)
      : series.slice_thickness;
    const tolerance =
      spacingBetweenPlanes != null && spacingBetweenPlanes > 0
        ? spacingBetweenPlanes / 2 + 0.001
        : 0.05;
    const anchorDistance = dot(delta(anchor), normal);
    if (
      Math.abs(anchorDistance) > tolerance ||
      [worldAt(plane.columns - 1, 0), worldAt(0, plane.rows - 1)].some(
        (point) => Math.abs(dot(delta(point), normal) - anchorDistance) > 0.05,
      )
    ) {
      continue;
    }
    const indices: number[] = [];
    let outside = false;
    for (const [start, length] of plane.runs) {
      for (let index = start; index < start + length; index++) {
        const point = delta(worldAt(index % plane.columns, Math.floor(index / plane.columns)));
        const column = Math.round(dot(point, x) / spacing[1]);
        const row = Math.round(dot(point, y) / spacing[0]);
        if (row < 0 || row >= rows || column < 0 || column >= columns) {
          outside = true;
          break;
        }
        indices.push(row * columns + column);
      }
      if (outside) break;
    }
    if (outside || !indices.length) {
      continue;
    }
    const preview = buildSegmentationPreviews(
      {
        rows,
        columns,
        segments: [
          {
            segmentIndex: sequence,
            sourceSegmentIndex: annotation.segment_index,
            condition,
            voxelCount: geometry.voxelCount,
            slices: [
              {
                seriesId,
                sliceIndex,
                sliceId: instance.instance_uid,
                runs: pixelIndicesToRuns(indices),
              },
            ],
          },
        ],
      },
      metadata,
      {},
    )[0];
    if (!preview) return null;
    return {
      ...preview,
      id: `saved-${annotation.annotation_id}`,
      measurements: {
        ...preview.measurements,
        volumeMm3: annotation.segmentation_stats?.volume_mm3 ?? null,
      },
    };
  }
  return null;
}
